import { useCallback, useEffect, useState, useRef } from "react";
import baseUrl from "../../../api/baseUrl";
import PaymentButton from "../../components/reminder/PayButton";
import { useNavigate } from "react-router-dom";
import PaymentIcon from "@mui/icons-material/Payment";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import BusinessIcon from "@mui/icons-material/Business";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import CheckIcon from "@mui/icons-material/Check";

const initialStartDate = new Date();
const initialEndDate = new Date();
initialEndDate.setMonth(initialEndDate.getMonth() + 1);

function PaymentData() {
  const [theme, setTheme] = useState<"light" | "dark">("light");

  useEffect(() => {
    const savedTheme = localStorage.getItem("theme") as "light" | "dark";
    if (savedTheme) setTheme(savedTheme);
  }, []);

  useEffect(() => {
    const script = document.createElement("script");
    script.src = "https://www.payhere.lk/lib/payhere.js";
    script.type = "text/javascript";
    script.async = true;
    document.body.appendChild(script);
    return () => {
      document.body.removeChild(script);
    };
  }, []);

  const navigate = useNavigate();
  const isDark = theme === "dark";
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const [formData, setformData] = useState({
    price: "",
    clinicName: "",
    title: "",
    lastName: "",
    firstName: "",
    email: "",
    address: "",
    orderId: "",
    phone: "",
    clinicid: "",
    months: 1,
    startDate: initialStartDate,
    endDate: initialEndDate,
  });

  // Click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsDropdownOpen(false);
      }
    };

    if (isDropdownOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isDropdownOpen]);

  const packageOptions = [
    { name: "1 month", value: 1 },
    { name: "3 months", value: 3 },
    { name: "6 months", value: 6 },
  ];

  const handleSelectPackage = useCallback((months: number) => {
    setformData((prevData) => {
      const start = new Date(prevData.startDate);
      const end = new Date(start);
      end.setMonth(end.getMonth() + months);

      return {
        ...prevData,
        months: months,
        endDate: end,
      };
    });
    setIsDropdownOpen(false);
  }, []);

  const selectedPackage = packageOptions.find((pkg) => pkg.value === formData.months);

  const onChangeHandler = useCallback(
    (e: React.ChangeEvent<HTMLSelectElement>) => {
      const { name, value } = e.target;
      const numericValue = parseInt(value);

      if (name === "months") {
        setformData((prevData) => {
          const start = new Date(prevData.startDate);
          const end = new Date(start);
          end.setMonth(end.getMonth() + numericValue);

          return {
            ...prevData,
            months: numericValue,
            endDate: end,
          };
        });
      } else {
        setformData((prevData) => ({
          ...prevData,
          [name]: value,
        }));
      }
    },
    []
  );

  useEffect(() => {
    baseUrl
      .get("/client/fee/getClinicPaymentbyId")
      .then((response) => {
        const emp = response?.data?.data;
        if (!emp) {
          console.warn("No payment data found.");
          return;
        }
        setformData((prevData) => {
          const start = new Date(emp.paymentendDate ?? new Date());
          const end = new Date(start);
          end.setMonth(end.getMonth() + 1);
          return {
            ...prevData,
            price: emp?.price || "",
            clinicName: emp?.clinicName || "",
            firstName: emp?.firstName || "",
            lastName: emp?.lastName || "",
            title: emp?.title || "",
            address: emp?.address || "",
            email: emp?.email || "",
            orderId: emp?.orderId || "",
            phone: emp?.phone || "",
            clinicid: emp?.clinicid || "",
            startDate: start,
            endDate: end,
          };
        });
      })
      .catch((error) => {
        console.log(error);
      });
  }, []);

  const onNavigate = () => {
    navigate("/client/dashboard");
  };

  return (
    <div className={`min-h-full flex items-center justify-center p-3 md:p-4 ${isDark ? "bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900" : "bg-gradient-to-br from-slate-50 via-primary/10 to-primaryLight/10"}`}>
      <div className={`w-full max-w-lg rounded-[8px] shadow-xl overflow-hidden ${isDark ? "bg-slate-800" : "bg-white"}`}>
        {/* Header */}
        <div className="bg-gradient-to-r from-primary to-primaryLight px-6 py-5">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-xl bg-white/20 flex items-center justify-center">
              <PaymentIcon sx={{ fontSize: 28 }} className="text-white" />
            </div>
            <div>
              <h1 className="text-xl font-bold text-white">Payment Details</h1>
              <p className="text-white/80 text-sm">Complete your subscription</p>
            </div>
          </div>
        </div>

        {/* Clinic Info */}
        <div className={`px-3 py-2.5 border-b ${isDark ? "border-slate-700 bg-slate-700/50" : "border-slate-200 bg-slate-50"}`}>
          <div className="flex items-center gap-2.5">
            <div className={`w-8 h-8 rounded-md flex items-center justify-center ${isDark ? "bg-slate-600" : "bg-primary/10"}`}>
              <BusinessIcon sx={{ fontSize: 16 }} className={isDark ? "text-primaryLight" : "text-primary"} />
            </div>
            <div>
              <p className={`text-[10px] uppercase tracking-wide ${isDark ? "text-slate-400" : "text-slate-500"}`}>Clinic Name</p>
              <p className={`font-semibold text-xs ${isDark ? "text-white" : "text-slate-800"}`}>{formData.clinicName}</p>
            </div>
          </div>
          <p className={`mt-2 text-xs ${isDark ? "text-slate-300" : "text-slate-600"}`}>
            {formData.title} {formData.firstName} {formData.lastName}
          </p>
        </div>

        {/* Content */}
        <div className="px-3 py-3 space-y-3">
          {/* Monthly Price */}
          <div className={`p-2.5 rounded-lg ${isDark ? "bg-slate-700" : "bg-primary/10"}`}>
            <p className={`text-xs ${isDark ? "text-slate-400" : "text-slate-600"}`}>Monthly Rental</p>
            <p className={`text-lg font-bold ${isDark ? "text-white" : "text-primary"}`}>
              Rs. {formData.price}
            </p>
          </div>

          {/* Package Select */}
          <div>
            <label className={`block text-xs font-semibold mb-2 ${isDark ? "text-slate-300" : "text-slate-700"}`}>
              Select your preferred package
            </label>
            <div className="relative" ref={dropdownRef}>
              <button
                type="button"
                onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                className={`w-full px-1 py-2.5 pr-3 pl-3 rounded-lg border-2 transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-primary/50 shadow-sm hover:shadow-md text-left text-xs font-medium flex items-center justify-between ${
                  isDropdownOpen
                    ? isDark
                      ? "bg-slate-700 border-primary text-white"
                      : "bg-gradient-to-r from-primary/10 to-primaryLight/10 border-primary text-slate-800"
                    : isDark
                    ? "bg-slate-700/80 border-slate-600 text-white hover:border-primary/50 hover:bg-slate-700"
                    : "bg-white border-slate-300 text-slate-800 hover:border-primary/60 hover:bg-gradient-to-r hover:from-primary/5 hover:to-primaryLight/5"
                }`}
              >
                <div className="flex items-center gap-2.5">
                  <div className={`${isDark ? "text-primaryLight/60" : "text-primary/60"}`}>
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                  <span>{selectedPackage?.name || "Select package"}</span>
                </div>
                <ArrowDropDownIcon
                  sx={{ fontSize: 20 }}
                  className={`transition-transform duration-300 ${isDropdownOpen ? "rotate-180" : ""} ${
                    isDark ? "text-primaryLight" : "text-primary"
                  }`}
                />
              </button>

              {/* Dropdown Menu */}
              {isDropdownOpen && (
                <div
                  className={`absolute z-50 w-full mt-2 rounded-lg shadow-xl border overflow-hidden ${
                    isDark
                      ? "bg-slate-800 border-slate-600"
                      : "bg-white border-slate-200 shadow-slate-300/30"
                  }`}
                >
                  <div className="py-1.5">
                    {packageOptions.map((option, index) => (
                      <button
                        key={index}
                        type="button"
                        onClick={() => handleSelectPackage(option.value)}
                        className={`w-full px-4 py-2.5 text-left text-xs font-medium transition-all duration-200 flex items-center justify-between ${
                          formData.months === option.value
                            ? isDark
                              ? "bg-primary/20 text-primaryLight"
                              : "bg-gradient-to-r from-primary/10 to-primaryLight/10 text-primary"
                            : isDark
                            ? "text-slate-300 hover:bg-slate-700"
                            : "text-slate-700 hover:bg-slate-50"
                        }`}
                      >
                        <span>{option.name}</span>
                        {formData.months === option.value && (
                          <CheckIcon
                            sx={{ fontSize: 18 }}
                            className={isDark ? "text-primaryLight" : "text-primary"}
                          />
                        )}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Fee Summary */}
          <div className={`rounded-lg border overflow-hidden ${isDark ? "border-slate-600" : "border-slate-200"}`}>
            <div className={`flex justify-between items-center px-3 py-2 ${isDark ? "bg-slate-700" : "bg-slate-50"}`}>
              <span className={`font-medium text-xs ${isDark ? "text-slate-300" : "text-slate-600"}`}>Total Amount</span>
              <span className={`text-base font-bold ${isDark ? "text-white" : "text-slate-800"}`}>
                Rs. {Number(formData.price) * formData.months}
              </span>
            </div>
            <div className={`px-3 py-2 flex items-center gap-1.5 border-t ${isDark ? "border-slate-600" : "border-slate-200"}`}>
              <CalendarTodayIcon sx={{ fontSize: 14 }} className={isDark ? "text-slate-400" : "text-slate-500"} />
              <span className={`text-xs ${isDark ? "text-slate-400" : "text-slate-500"}`}>From:</span>
              <span className={`text-xs font-medium ${isDark ? "text-slate-200" : "text-slate-700"}`}>
                {formData.startDate.toLocaleDateString()}
              </span>
            </div>
            <div className={`px-3 py-2 flex items-center gap-1.5 border-t ${isDark ? "border-slate-600" : "border-slate-200"}`}>
              <CalendarTodayIcon sx={{ fontSize: 14 }} className={isDark ? "text-slate-400" : "text-slate-500"} />
              <span className={`text-xs ${isDark ? "text-slate-400" : "text-slate-500"}`}>Valid till:</span>
              <span className={`text-xs font-medium ${isDark ? "text-slate-200" : "text-slate-700"}`}>
                {formData.endDate.toLocaleDateString()}
              </span>
            </div>
          </div>
        </div>

        {/* Footer / Buttons */}
        <div className={`px-3 py-2 flex gap-2 justify-end border-t ${isDark ? "bg-slate-700 border-slate-600" : "bg-slate-50 border-slate-200"}`}>
          <button
            onClick={onNavigate}
            className={`px-3 py-1.5 rounded-lg font-medium transition-colors text-xs h-[32px] ${
              isDark
                ? "text-slate-200 bg-slate-600 border border-slate-500 hover:bg-slate-500"
                : "text-slate-700 bg-white border border-slate-300 hover:bg-slate-100"
            }`}
          >
            Cancel
          </button>
          <PaymentButton formData={formData} />
        </div>
      </div>
    </div>
  );
}

export default PaymentData;
